import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import whatsappNotificationService from '../../services/whatsappNotificationService';
import AdminNavigation from '../../components/ui/AdminNavigation';
import Button from '../../components/ui/Button';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function NotificationLogsAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    provider: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [notificationsList, statistics] = await Promise.all([
        whatsappNotificationService?.getNotifications(filters),
        whatsappNotificationService?.getNotificationStats(filters?.dateFrom, filters?.dateTo)
      ]);

      setNotifications(notificationsList || []);
      setStats(statistics);
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (notificationId) => {
    try {
      setError('');
      setSuccess('');
      
      await whatsappNotificationService?.retryNotification(notificationId);
      
      setSuccess('Notification renvoyée avec succès');
      loadData();
    } catch (err) {
      setError(err?.message || 'Erreur lors du renvoi');
    }
  };

  const handleDelete = async (notificationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette notification ?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      await whatsappNotificationService?.deleteNotification(notificationId);
      
      setSuccess('Notification supprimée avec succès');
      loadData();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la suppression');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Date', 'Référence', 'Téléphone', 'Message', 'Statut', 'Provider'];
    const rows = notifications?.map(n => [
      new Date(n.created_at)?.toLocaleString('fr-FR'),
      n?.shipment_reference || 'N/A',
      n?.phone_number,
      n?.message,
      n?.notification_status,
      n?.provider
    ]);

    const csv = [headers, ...rows]?.map(row => row?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notifications-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  if (loading && !notifications?.length) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Logs & Analyses des Notifications</h1>
              <p className="mt-2 text-gray-600">Suivi et analyse des envois WhatsApp</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleExportCSV}
                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                disabled={!notifications?.length}
              >
                Exporter CSV
              </Button>
              <Button
                onClick={() => navigate('/admin/whatsapp-config')}
                className="bg-green-600 text-white hover:bg-green-700"
              >
                Configuration
              </Button>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <StatCard title="Total" value={stats?.total} color="blue" />
            <StatCard title="Envoyés" value={stats?.sent} color="green" />
            <StatCard title="En attente" value={stats?.pending} color="yellow" />
            <StatCard title="Échoués" value={stats?.failed} color="red" />
          </div>
        )}

        {/* Provider Statistics */}
        {stats?.byProvider && Object.keys(stats?.byProvider)?.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistiques par Provider</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(stats?.byProvider)?.map(([provider, providerStats]) => (
                <div key={provider} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2 capitalize">{provider}</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-medium">{providerStats?.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Envoyés:</span>
                      <span className="font-medium text-green-600">{providerStats?.sent}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Échoués:</span>
                      <span className="font-medium text-red-600">{providerStats?.failed}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <select
                value={filters?.status}
                onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Tous</option>
                <option value="pending">En attente</option>
                <option value="sent">Envoyé</option>
                <option value="failed">Échoué</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
              <select
                value={filters?.provider}
                onChange={(e) => setFilters({ ...filters, provider: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Tous</option>
                <option value="n8n">n8n</option>
                <option value="make">Make.com</option>
                <option value="manychat">ManyChat</option>
                <option value="whatsapp_cloud">WhatsApp Cloud</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
              <input
                type="date"
                value={filters?.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
              <input
                type="date"
                value={filters?.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Notifications Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Référence</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notifications?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      Aucune notification trouvée
                    </td>
                  </tr>
                ) : (
                  notifications?.map((notification) => (
                    <tr key={notification?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDistanceToNow(new Date(notification.created_at), { 
                          addSuffix: true,
                          locale: fr 
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {notification?.shipment_reference || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {notification?.phone_number}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {notification?.message}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          notification?.notification_status === 'sent' ?'bg-green-100 text-green-800'
                            : notification?.notification_status === 'pending' ?'bg-yellow-100 text-yellow-800' :'bg-red-100 text-red-800'
                        }`}>
                          {notification?.notification_status === 'sent' ? 'Envoyé' :
                           notification?.notification_status === 'pending' ? 'En attente' : 'Échoué'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {notification?.provider}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          {(notification?.notification_status === 'failed' || notification?.notification_status === 'pending') && (
                            <button
                              onClick={() => handleRetry(notification?.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Renvoyer
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(notification?.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    red: 'bg-red-50 text-red-700'
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <p className="text-sm font-medium text-gray-600">{title}</p>
      <p className={`text-3xl font-bold mt-2 ${colorClasses?.[color]}`}>
        {value}
      </p>
    </div>
  );
}